import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

interface Service {
  id: number;
  title?: string;
  name?: string;
  price: number;
}

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  success: "#10b981",
  successBg: "#dcfce7",
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: `0.5px solid ${colors.borderLight}`,
  borderRadius: 12,
};

const buttonBase: React.CSSProperties = {
  padding: "9px 16px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  border: "1px solid transparent",
  transition: "all .15s ease",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 12px",
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  fontSize: 13,
  color: colors.text,
  background: colors.bgSubtle,
  outline: "none",
  transition: "border-color .15s ease, background .15s ease",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 500,
  color: colors.textMuted,
  marginBottom: 6,
};

const AddPromotion = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<string>("");
  const [selectedServiceId, setSelectedServiceId] = useState<number | "">("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [promotionalPrice, setPromotionalPrice] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [termsConditions, setTermsConditions] = useState("");
  const navigate = useNavigate();

  // Charger les services selon le type sélectionné
  useEffect(() => {
    const fetchServices = async () => {
      if (!selectedServiceType) {
        setServices([]);
        return;
      }

      try {
        let endpoint = "";
        switch (selectedServiceType) {
          case "trip":
            endpoint = `${API_URL}/trips`;
            break;
          case "visa_pack":
            endpoint = `${API_URL}/visa-packs`;
            break;
          case "car_rental":
            endpoint = `${API_URL}/cars`;
            break;
          default:
            return;
        }

        const response = await axios.get(endpoint);
        const servicesData = response.data.data?.data || response.data.data || response.data;
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des services:", error);
      }
    };

    fetchServices();
  }, [selectedServiceType]);

  // Mettre à jour le prix original quand un service est sélectionné
  useEffect(() => {
    if (selectedServiceId && services.length > 0) {
      const selectedService = services.find((service) => service.id === selectedServiceId);
      if (selectedService) {
        setOriginalPrice(selectedService.price.toString());
      }
    }
  }, [selectedServiceId, services]);

  const handleSave = async () => {
    try {
      const promotionData = {
        title,
        description,
        service_type: selectedServiceType,
        service_id: selectedServiceId,
        original_price: parseFloat(originalPrice),
        promotional_price: parseFloat(promotionalPrice),
        start_date: startDate,
        end_date: endDate,
        max_uses: maxUses ? parseInt(maxUses) : null,
        promo_code: promoCode || null,
        terms_conditions: termsConditions || null,
      };

      await axios.post(`${API_URL}/promotions`, promotionData);

      toast.success("Promotion created successfully!");
      navigate("/admin/list-promotion");
    } catch (error: any) {
      console.error("Erreur lors de la création de la promotion:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Error creating promotion");
    }
  };

  // Calculer le pourcentage de réduction
  const calculateDiscount = () => {
    if (!originalPrice || !promotionalPrice) return "0";
    const original = parseFloat(originalPrice);
    const promo = parseFloat(promotionalPrice);
    if (original === 0) return "0";
    return (((original - promo) / original) * 100).toFixed(2);
  };

  return (
    <>
      {/* Grille : Left (20%), Center (60%), Right (20%) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "20% 60% 20%",
          gap: "16px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Colonne Gauche (20%) - Navigation / Conseils */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ ...cardStyle, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 10px 0", color: colors.text }}>
              Quick Navigation
            </h3>
            <button
              type="button"
              onClick={() => navigate("/admin/list-promotion")}
              style={{
                ...buttonBase,
                width: "100%",
                background: colors.bgSubtle,
                border: `1px solid ${colors.border}`,
                color: colors.text,
              }}
            >
              &larr; Back to List
            </button>
          </div>

          <div style={{ ...cardStyle, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 8px 0", color: colors.text }}>
              Help
            </h3>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
              Select a service type first to dynamically load related trips, visa packages, or cars.
            </p>
          </div>
        </div>

        {/* Colonne Centre (60%) - Formulaire Principal */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Header de la carte */}
          <div style={{ ...cardStyle, padding: 16 }}>
            <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: colors.text }}>
              Create New Promotion
            </h1>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: "4px 0 0 0" }}>
              Add promotional offers for trips, visa packs, or car rentals
            </p>
          </div>

          {/* Formulaire */}
          <div style={{ ...cardStyle, padding: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Type de Service & Sélection Service */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Service Type *</label>
                  <select
                    value={selectedServiceType}
                    onChange={(e) => {
                      setSelectedServiceType(e.target.value);
                      setSelectedServiceId("");
                      setOriginalPrice("");
                    }}
                    style={inputStyle}
                  >
                    <option value="">-- Choose Service Type --</option>
                    <option value="trip">Trip</option>
                    <option value="visa_pack">Visa Pack</option>
                    <option value="car_rental">Car Rental</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Service *</label>
                  <select
                    value={selectedServiceId}
                    onChange={(e) => setSelectedServiceId(Number(e.target.value))}
                    disabled={!selectedServiceType}
                    style={{
                      ...inputStyle,
                      opacity: !selectedServiceType ? 0.6 : 1,
                      cursor: !selectedServiceType ? "not-allowed" : "pointer",
                    }}
                  >
                    <option value="">-- Choose a Service --</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.title || service.name} - ${service.price}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Title */}
              <div>
                <label style={labelStyle}>Promotion Title *</label>
                <input
                  type="text"
                  placeholder="Enter promotion title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  style={inputStyle}
                />
              </div>

              {/* Description */}
              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  placeholder="Enter promotion description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>

              {/* Pricing */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Original Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Promotional Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={promotionalPrice}
                    onChange={(e) => setPromotionalPrice(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Discount Display */}
              {originalPrice && promotionalPrice && (
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: colors.successBg,
                    border: `1px solid ${colors.success}`,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#15803d",
                  }}
                >
                  Discount: {calculateDiscount()}% off
                </div>
              )}

              {/* Dates */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Start Date *</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>End Date *</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Additional Options */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Max Uses (Optional)</label>
                  <input
                    type="number"
                    placeholder="Unlimited if empty"
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Promo Code (Optional)</label>
                  <input
                    type="text"
                    placeholder="Auto-generated if empty"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Terms & Conditions */}
              <div>
                <label style={labelStyle}>Terms & Conditions</label>
                <textarea
                  placeholder="Enter terms and conditions for this promotion"
                  value={termsConditions}
                  onChange={(e) => setTermsConditions(e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>

              {/* Actions */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  marginTop: 10,
                  paddingTop: 16,
                  borderTop: `1px solid ${colors.borderLight}`,
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/admin/list-promotion")}
                  style={{
                    ...buttonBase,
                    background: "#fff",
                    border: `1px solid ${colors.border}`,
                    color: colors.textMuted,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  style={{
                    ...buttonBase,
                    background: colors.primary,
                    color: "#fff",
                  }}
                >
                  Create Promotion
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Colonne Droite (20%) - Information Complémentaire */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ ...cardStyle, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 10px 0", color: colors.text }}>
              Summary Preview
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12, color: colors.textMuted }}>
              <div>
                <span style={{ fontWeight: 500 }}>Type: </span>
                <span>{selectedServiceType || "Not selected"}</span>
              </div>
              <div>
                <span style={{ fontWeight: 500 }}>Discount: </span>
                <span style={{ color: colors.success, fontWeight: 600 }}>
                  {calculateDiscount()}%
                </span>
              </div>
              <div>
                <span style={{ fontWeight: 500 }}>Code: </span>
                <span style={{ fontFamily: "monospace" }}>{promoCode || "Auto"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </>
  );
};

export default AddPromotion;